import { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  formatNumber,
  formatPercentage,
  formatReportDateShort,
} from "@/lib/format";
import type { DailyRun } from "@/lib/types";

interface PercentageTrendChartProps {
  runs: DailyRun[];
  loading?: boolean;
}

export function PercentageTrendChart({ runs, loading }: PercentageTrendChartProps) {
  const data = useMemo(() => {
    return [...runs]
      .filter((run) => run.status === "completed")
      .sort((a, b) => a.report_date.localeCompare(b.report_date))
      .map((run) => ({
        date: run.report_date,
        percentage: Number(run.daily_score),
        estimated: run.estimated_antisemitic_post_count,
        analyzed: run.sample_size_analyzed,
      }));
  }, [runs]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Daily percentage trend</CardTitle>
        </CardHeader>
        <CardContent className="h-[320px]">
          <Skeleton className="h-full w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-baseline justify-between pb-2">
        <CardTitle className="text-base">Daily percentage trend</CardTitle>
        <p className="text-xs text-muted-foreground">
          % of sampled posts classified as antisemitic, plus estimated absolute
          volume
        </p>
      </CardHeader>
      <CardContent className="h-[320px] pl-2 pr-4">
        {data.length === 0 ? (
          <EmptyState />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 12, right: 12, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="percentageFill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="var(--color-chart-1)"
                    stopOpacity={0.45}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-chart-1)"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-border)"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={formatReportDateShort}
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                minTickGap={24}
              />
              <YAxis
                yAxisId="percentage"
                domain={[0, "auto"]}
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={40}
                tickFormatter={(value: number) => `${value.toFixed(0)}`}
              />
              <YAxis
                yAxisId="count"
                orientation="right"
                stroke="var(--color-muted-foreground)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={48}
                tickFormatter={(value: number) => formatNumber(value)}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: "var(--color-border)" }} />
              <Area
                yAxisId="percentage"
                type="monotone"
                dataKey="percentage"
                stroke="var(--color-chart-1)"
                strokeWidth={2}
                fill="url(#percentageFill)"
                name="Daily percentage"
              />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="estimated"
                stroke="var(--color-chart-3)"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
                name="Estimated antisemitic posts"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
      <p className="max-w-xs">
        No completed runs yet. The chart will populate as the daily tracker
        completes runs.
      </p>
    </div>
  );
}

interface TooltipPayloadItem {
  name?: string;
  dataKey?: string;
  value?: number;
  color?: string;
  payload?: { date: string };
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const date =
    payload[0]?.payload?.date ??
    (typeof label === "string" ? label : undefined);
  return (
    <div className="rounded-md border border-border/60 bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-foreground">
        {formatReportDateShort(date)}
      </div>
      {payload.map((item) => {
        const isPercentage = item.dataKey === "percentage";
        const value = item.value ?? 0;
        return (
          <div
            key={item.dataKey}
            className="flex items-center gap-2 text-muted-foreground"
          >
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: item.color }}
            />
            <span>{item.name}:</span>
            <span className="ml-1 font-medium tabular-nums text-foreground">
              {isPercentage ? formatPercentage(value) : formatNumber(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
